import { authStore, getUser } from "@/lib/auth/store.tsx";
import { formatTimestampAuto } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { createFileRoute } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Level1Icon, Level2Icon, Level3Icon } from "~/components/icons";
import { Separator } from "~/components/ui/separator";
import { UpdateUserProfileType, updateProfile } from "./-api";
import { MemberLevel } from "./-api";
import { AvatarEditor } from "./-comp/avatar-editor";
import { UsernameEditor } from "./-comp/username-editor";
export const Route = createFileRoute("/$lang/profile/account")({
	component: ProfileAccount,
});

const levelMap = {
	[MemberLevel.STANDARD]: {
		icon: <Level1Icon className="w-9 h-auto" />,
		name: m.profile_account_level_standard(),
	},
	[MemberLevel.PREMIUM]: {
		icon: <Level2Icon className="w-9 h-auto" />,
		name: m.profile_account_level_premium(),
	},
	[MemberLevel.SUPREME]: {
		icon: <Level3Icon className="w-9 h-auto" />,
		name: m.profile_account_level_supreme(),
	},
};

function ProfileAccount() {
	const user = useStore(authStore, (s) => s.user);

	const [avatar, setAvatar] = useState(user?.avatar || "");
	const [username, setUsername] = useState(user?.name || "");
	const [editUsernameError, setEditUsernameError] = useState<string | null>(
		null,
	);
	const [editing, setEditing] = useState(false);

	// 监听用户信息变化
	useEffect(() => {
		if (user) {
			setAvatar(user.avatar || "");
			setUsername(user.name || "");
		}
	}, [user]);

	const handleUpdateProfile = async (
		newName: string,
		type: keyof typeof UpdateUserProfileType,
		newAvatar?: string,
	) => {
		// 手动编辑用户名时不显示错误 toast，只显示 editUsernameError
		const showErrorToast =
			type !== UpdateUserProfileType.UPDATE_USER_PROFILE_TYPE_NORMAL;

		// 随机生成时不需要传递 name，由服务器端处理
		const nameToSend =
			type === UpdateUserProfileType.UPDATE_USER_PROFILE_TYPE_ROLL_NAME
				? ""
				: newName;

		const res = await updateProfile({
			name: nameToSend,
			type,
			avatar: newAvatar ? newAvatar : avatar,
			showErrorToast,
		});

		if (res.code !== 0) {
			setEditUsernameError(res.msg);
			// 出错时抛出错误，让调用方知道操作失败
			throw new Error(res.msg);
		}
		setEditUsernameError(null);
		setTimeout(() => {
			toast.success(m.profile_account_update_success());
		}, 2000);

		await getUser();
		// 只有在成功时才关闭编辑状态
		if (type === UpdateUserProfileType.UPDATE_USER_PROFILE_TYPE_NORMAL) {
			setEditing(false);
		}
	};

	return (
		<div className="min-h-[500px] px-8 py-4 md:py-10 ">
			<div className="">
				<div className="flex flex-col gap-10 min-w-[200px] max-md:gap-4">
					<UsernameEditor
						username={username}
						setUsername={setUsername}
						editing={editing}
						setEditing={setEditing}
						onSubmit={(type: keyof typeof UpdateUserProfileType) =>
							handleUpdateProfile(username, type, avatar)
						}
						editUsernameError={editUsernameError}
						setEditUsernameError={setEditUsernameError}
					/>

					<Separator />

					<AvatarEditor
						avatar={avatar}
						setAvatar={setAvatar}
						onSubmit={(
							type: keyof typeof UpdateUserProfileType,
							_avatar?: string,
						) => handleUpdateProfile(username, type, _avatar)}
					/>

					<Separator />
					<div className="flex gap-4 max-md:flex-col max-md:items-start">
						<div className="text-t2 w-[80px] text-sm font-medium capitalize">
							{user?.login_type}
						</div>
						<div className="text-sm text-t1">
							{user?.third_user_info?.email ??
								user?.third_user_info?.name ??
								"-"}
						</div>
					</div>

					<Separator />
					<div className="flex gap-4 max-md:flex-col max-md:items-start md:items-end">
						<div className="text-t2 w-[80px] text-sm font-medium">
							{m.profile_account_member_level()}
						</div>
						{user?.member_level &&
						levelMap[user.member_level as keyof typeof levelMap] ? (
							<div className="flex gap-2 items-end">
								{levelMap[user.member_level as keyof typeof levelMap].icon}
								<span className="text-sm text-t1">
									{levelMap[user.member_level as keyof typeof levelMap].name}
								</span>
							</div>
						) : (
							<span className="text-sm text-t1">-</span>
						)}
					</div>
					<Separator />
					<div className="flex gap-4 max-md:flex-col max-md:items-start">
						<div className="text-t2 w-[80px] text-sm font-medium">
							{m.profile_account_member_period()}
						</div>
						{user?.member_start_time && user?.member_end_time ? (
							<span className="text-sm text-t1">
								{formatTimestampAuto(user?.member_start_time, "YYYY-MM-DD")}
								<span className="mx-2.5">{m.profile_to()}</span>
								{formatTimestampAuto(user?.member_end_time, "YYYY-MM-DD")}
							</span>
						) : (
							<span className="text-sm text-t1">-</span>
						)}
					</div>
				</div>
				{/* <Separator /> */}
			</div>
		</div>
	);
}
